import { Link } from 'react-router-dom';
import { StatusChip } from '../../components/index.js';

/**
 * Presentational modules for the RAPID v2 Overview ("Command Snapshot") redesign.
 * Mirrors the as-built Metrics/Diagnosis view-switcher pattern (MetricsSections.jsx): an always-on
 * summary panel + the six-week phase rail, a section nav whose Expand-all/Collapse button is a
 * sibling OUTSIDE the role=tablist (axe), and one view per section rendered mounted-but-hidden so
 * existing testids/strings keep resolving. All copy is seeded/derived; pure render functions.
 *
 * The phase rail (RapidPhaseRail) and QuickStat are private to RapidPages.jsx, so the owning
 * OverviewTab renders the rail itself (always-on, kept visible by default for the e2e contract) and
 * passes already-shaped data into these views.
 */

export const OVERVIEW_SECTIONS = [
  { key: 'summary', label: 'Snapshot' },
  { key: 'operating', label: 'Operating Picture' },
  { key: 'decisions', label: 'Decisions & Blockers' },
  { key: 'activity', label: 'Activity History' },
  { key: 'handoff', label: 'Handoff Preview' },
];

// ---------------------------------------------------------------------------
// Section nav (view-switcher — mirrors MetricsSectionNav)
// ---------------------------------------------------------------------------

export function OverviewSectionNav({ active, onSelect }) {
  const expanded = active === 'all';
  return (
    <nav className="overview-section-nav" data-testid="overview-section-nav">
      <div className="overview-nav-tabs" role="tablist" aria-label="Overview sections">
        {OVERVIEW_SECTIONS.map((section) => (
          <button
            key={section.key}
            type="button"
            role="tab"
            aria-selected={active === section.key}
            className={`overview-nav-chip${active === section.key ? ' is-active' : ''}`}
            data-testid={`overview-nav-${section.key}`}
            onClick={() => onSelect(section.key)}
          >
            {section.label}
          </button>
        ))}
      </div>
      <button
        type="button"
        className={`overview-nav-chip overview-nav-expand${expanded ? ' is-active' : ''}`}
        data-testid="overview-expand-all"
        aria-pressed={expanded}
        onClick={() => onSelect(expanded ? 'summary' : 'all')}
      >
        {expanded ? 'Collapse' : 'Expand all'}
      </button>
    </nav>
  );
}

// ---------------------------------------------------------------------------
// Always-on Executive Snapshot
// ---------------------------------------------------------------------------

export function OverviewSummaryPanel({
  phaseLabel,
  health,
  score,
  recommendation,
  daysRemaining,
  weekLabel,
  closeoutPercent,
  closeoutComplete,
  closeoutTotal,
  valueDisplay,
  costAvoidanceLabel,
  openBlockers,
  nextMove,
}) {
  return (
    <section className="rapid-panel overview-summary-panel" data-testid="overview-summary-panel">
      <div className="overview-summary-head">
        <div className="eyebrow">Command snapshot</div>
        <h3 className="overview-summary-headline">Where this RAPID stands right now</h3>
      </div>

      <div className="overview-summary-stats">
        <div className="overview-summary-stat is-primary">
          <span className="k">Phase</span>
          <span className="v is-text" data-testid="overview-summary-phase">
            {phaseLabel}
          </span>
          <span className="sub">
            <StatusChip status={health}>{health}</StatusChip>
          </span>
        </div>
        <div className="overview-summary-stat">
          <span className="k">Criteria score</span>
          <span className="v" data-testid="overview-summary-score">
            {score}
          </span>
          <span className="sub">{recommendation}</span>
        </div>
        <div className="overview-summary-stat">
          <span className="k">Days remaining</span>
          <span className="v" data-testid="overview-summary-days">
            {daysRemaining}
          </span>
          <span className="sub">{weekLabel}</span>
        </div>
        <div className="overview-summary-stat">
          <span className="k">Closeout readiness</span>
          <span className="v" data-testid="overview-summary-closeout">
            {closeoutPercent}%
          </span>
          <span className="sub">
            {closeoutComplete} / {closeoutTotal} complete
          </span>
        </div>
      </div>

      <div className="overview-summary-notes">
        <div className="overview-summary-note is-safe">
          <h4>Value case</h4>
          <p>
            {valueDisplay} estimated value ({costAvoidanceLabel} modeled cost avoidance), pending
            source validation.
          </p>
        </div>
        <div className="overview-summary-note is-caveat">
          <h4>Open decisions &amp; blockers</h4>
          <p>
            {openBlockers} open {openBlockers === 1 ? 'item needs' : 'items need'} a sponsor
            decision or barrier removal before closeout.
          </p>
        </div>
      </div>

      <div className="overview-summary-next" data-testid="overview-summary-next-move">
        <span className="chip outline sm">Next required move</span>
        <p>{nextMove}</p>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Operating Picture
// ---------------------------------------------------------------------------

export function OperatingPictureView({ rapid, approvedLabel }) {
  return (
    <div className="rapid-grid two-up">
      <section className="rapid-panel">
        <div className="eyebrow">Operating picture</div>
        <h3 data-testid="rapid-prototype-hero">{rapid.title}</h3>
        <p>{rapid.problemStatement}</p>
        {rapid.northStar ? (
          <p className="overview-northstar">
            <strong>North Star:</strong> {rapid.northStar}
          </p>
        ) : null}
        <ul className="rapid-inline-list">
          <li>
            <strong>Sponsor:</strong> {rapid.functionalSponsorLabel}
          </li>
          <li>
            <strong>RAPID Lead:</strong> {rapid.rapidLeadLabel}
          </li>
          <li>
            <strong>Executive:</strong> {rapid.executiveSponsorLabel}
          </li>
          <li>
            <strong>LOE:</strong> {rapid.loe}
          </li>
        </ul>
      </section>
      <section className="rapid-panel">
        <div className="eyebrow">Approval &amp; delegation</div>
        <h3>{rapid.approval?.approvalState}</h3>
        <p>{rapid.approval?.delegationMemo?.memoStatus}</p>
        <ul className="rapid-inline-list">
          <li>
            <strong>Approver:</strong> {rapid.approval?.approver}
          </li>
          <li>
            <strong>Approved:</strong> {approvedLabel}
          </li>
          <li>
            <strong>Defined scope:</strong> {rapid.approval?.delegationMemo?.definedScope}
          </li>
        </ul>
        {/* External UAT FINDING-014: the scope boundary is stated, not implied. */}
        <p className="muted small">
          Work outside this defined scope requires separate approval before execution.
        </p>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Decisions & Blockers
// ---------------------------------------------------------------------------

export function DecisionsBlockersView({ barriers }) {
  return (
    <section className="rapid-panel">
      <div className="rapid-section-heading">
        <div>
          <div className="eyebrow">Barriers</div>
          <h3>Decisions and blockers</h3>
        </div>
        <div className="rapid-chip-row">
          <Link className="btn ghost sm" to="?tab=governance&gv=decisions">
            Open Governance decisions
          </Link>
          <Link className="btn ghost sm" to="?tab=closeout">
            Open Closeout
          </Link>
        </div>
      </div>
      <ul className="rapid-record-list">
        {barriers.map((barrier) => (
          <li key={barrier.id}>
            <div className="row spread">
              <strong>{barrier.barrier}</strong>
              <StatusChip status={barrier.status}>{barrier.status}</StatusChip>
            </div>
            <div className="muted small">
              {barrier.id} · {barrier.owner} · {barrier.severity}
            </div>
            <p>{barrier.decisionNeeded}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Activity History
// ---------------------------------------------------------------------------

export function ActivityHistoryView({ entries }) {
  return (
    <section className="rapid-panel">
      <div className="rapid-section-heading">
        <div>
          <div className="eyebrow">Activity history</div>
          <h3>Change history</h3>
        </div>
      </div>
      <ul className="rapid-record-list">
        {entries.map((entry) => (
          <li key={entry.key}>
            <div className="muted small">{entry.date}</div>
            <strong>{entry.actor}</strong>
            <p>{entry.text}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Handoff Preview
// ---------------------------------------------------------------------------

export function HandoffPreviewView({ preview }) {
  return (
    <section className="rapid-panel">
      <div className="eyebrow">Handoff preview</div>
      <h3>{preview.label}</h3>
      <p>{preview.narrative}</p>
      <div className="rapid-chip-row">
        <span className="chip outline sm">{preview.projectId}</span>
        <span className="chip outline sm">{preview.status}</span>
      </div>
      <Link className="btn primary sm" to={`/projects/${preview.projectId}`}>
        Open {preview.projectId}
      </Link>
    </section>
  );
}
